import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import agent from '../api/axios';

interface SubscriptionPlan {
  id: string;
  name: string;
  price: number;
  term: string;
  stripePriceId: string;
  active: boolean;
  days?: number;
  description?: string;
}

interface UserSubscription {
  hasSubscription: boolean;
  subscriptionId?: string;
  status?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd?: boolean;
  canceledAt?: string;
  planName?: string;
  planPrice?: number;
  interval?: string;
  term?: string;
  message?: string;
  // Pending downgrade info
  pendingDowngrade?: boolean;
  pendingPlanName?: string;
  pendingPlanPrice?: number;
  pendingChangeDate?: string;
}

interface SubscriptionsState {
  plans: SubscriptionPlan[];
  userSubscription: UserSubscription | null;
  loading: boolean;
  error: string | null;
}

const initialState: SubscriptionsState = {
  plans: [],
  userSubscription: null,
  loading: false,
  error: null,
};

export const fetchSubscriptionPlans = createAsyncThunk(
  'subscriptions/fetchPlans',
  async () => {
    const response = await agent.Subscriptions.get();
    return response;
  }
);

export const fetchUserSubscription = createAsyncThunk(
  'subscriptions/fetchUserSubscription',
  async () => {
    const response = await agent.Business.getMySubscription();
    return response;
  }
);

export const cancelSubscription = createAsyncThunk(
  'subscriptions/cancel',
  async () => {
    const response = await agent.Business.cancelSubscription();
    return response;
  }
);

export const reactivateSubscription = createAsyncThunk(
  'subscriptions/reactivate',
  async () => {
    const response = await agent.Business.reactivateSubscription();
    return response;
  }
);

export const changeSubscription = createAsyncThunk(
  'subscriptions/change',
  async (planId: string) => {
    const response = await agent.Business.changeSubscription(planId);
    return response;
  }
);

const subscriptionsSlice = createSlice({
  name: 'subscriptions',
  initialState,
  reducers: {
    clearSubscriptions: (state) => {
      state.plans = [];
      state.userSubscription = null;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch plans
      .addCase(fetchSubscriptionPlans.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSubscriptionPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchSubscriptionPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch plans';
      })
      // Fetch user subscription
      .addCase(fetchUserSubscription.fulfilled, (state, action) => {
        state.userSubscription = action.payload;
      })
      .addCase(fetchUserSubscription.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to fetch subscription';
      })
      // Cancel subscription
      .addCase(cancelSubscription.fulfilled, (state) => {
        if (state.userSubscription) {
          state.userSubscription.cancelAtPeriodEnd = true;
          state.userSubscription.status = 'canceling';
        }
      })
      // Reactivate subscription
      .addCase(reactivateSubscription.fulfilled, (state) => {
        if (state.userSubscription) {
          state.userSubscription.cancelAtPeriodEnd = false;
          state.userSubscription.status = 'active';
        }
      });
  },
});

export const { clearSubscriptions } = subscriptionsSlice.actions;
export default subscriptionsSlice.reducer;
